import { supabase } from './supabase'
import type {
  Profile,
  ProfileWithMetadata,
  NotificationRule,
  NotificationRuleWithMetadata,
  Notification,
  TimelineNotification,
  AlertDetail,
  Publication,
  InstitutionCategory,
  PublicationType,
  CategoryPublicationType,
  ExtractionRecipe,
  PublicationExtraction,
  InboxMessage,
  CreateNotificationRuleRequest,
  UpdateNotificationRuleRequest,
  UpdateProfileRequest,
  PaginatedResponse,
  PaginationParams,
  TextFilter,
  RulePreview,
  RuleFilterBreakdown,
  RuleInstitution,
} from '@/types'

/**
 * Base API Service
 * Provides typed wrappers around Supabase RPC calls and queries.
 * All functions use the authenticated user's session.
 */

// =============================================================================
// ERROR HANDLING
// =============================================================================

export class ApiError extends Error {
  constructor(
    message: string,
    public code?: string,
    public details?: unknown,
    public isAborted?: boolean
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Check if an error is an AbortError (request was cancelled)
 * This happens when:
 * - User logs out while requests are in-flight
 * - Component unmounts before request completes
 * - React Query cancels a stale query
 */
function isAbortError(error: unknown): boolean {
  if (error instanceof Error) {
    // Check for AbortError by name or message
    if (error.name === 'AbortError') return true
    if (error.message.includes('AbortError')) return true
    if (error.message.includes('signal is aborted')) return true
    if (error.message.includes('aborted')) return true
  }
  // Also check object-shaped errors (like PostgrestError from Supabase)
  // PostgrestError is not an Error instance but has message, code, details properties
  if (error && typeof error === 'object' && 'message' in error) {
    const msg = (error as { message: string }).message
    if (typeof msg === 'string') {
      if (msg.includes('AbortError')) return true
      if (msg.includes('signal is aborted')) return true
    }
  }
  return false
}

function handleError(error: unknown): never {
  // Check if this is an abort error (e.g., from logout or navigation)
  if (isAbortError(error)) {
    // Don't log abort errors as they're expected during logout/navigation
    const abortError = new ApiError('Request was cancelled', 'ABORTED', error, true)
    throw abortError
  }

  // Log error for debugging
  console.error('API Error:', error)

  if (error instanceof Error) {
    throw new ApiError(error.message, undefined, error)
  }
  // Handle Supabase PostgrestError which has message, code, details properties
  if (error && typeof error === 'object' && 'message' in error) {
    const pgError = error as { message: string; code?: string; details?: string }
    throw new ApiError(pgError.message, pgError.code, pgError.details)
  }
  throw new ApiError('An unexpected error occurred', undefined, error)
}

// =============================================================================
// PROFILE
// =============================================================================

/**
 * Current user's profile via get_my_profile(): the profiles view plus
 * server-computed tier_limits (trial and founding perks) and founding_offer.
 */
export async function getProfile(): Promise<ProfileWithMetadata> {
  const { data, error } = await supabase.rpc('get_my_profile')

  if (error) handleError(error)
  if (!data) throw new ApiError('Profile not found', 'PGRST116')
  return data as ProfileWithMetadata
}

/** Record the Sign-up entry of a Google sign-up (#174; the server keeps it only for new accounts) */
export async function recordSignupEntry(entry: string): Promise<void> {
  const { error } = await supabase.rpc('record_signup_entry', { p_entry: entry })

  if (error) handleError(error)
}

/** Accept the current Terms of Service (in-app banner); returns the updated profile */
export async function acceptCurrentTerms(): Promise<ProfileWithMetadata> {
  const { data, error } = await supabase.rpc('accept_current_terms')

  if (error) handleError(error)
  return data as ProfileWithMetadata
}

export async function updateProfile(
  updates: UpdateProfileRequest
): Promise<Profile> {
  const { data, error } = await supabase.rpc('update_my_profile', {
    p_full_name: updates.full_name ?? null,
    p_disabled_channels: updates.disabled_channels ?? null,
    p_marketing_emails_enabled: updates.marketing_emails_enabled ?? null,
  })

  if (error) handleError(error)
  return data as Profile
}

/** Switch the rest of the trial to Professional (granted instantly) */
export async function requestProTrial(reason: string): Promise<ProfileWithMetadata> {
  const { data, error } = await supabase.rpc('request_pro_trial', { p_reason: reason })

  if (error) handleError(error)
  return data as ProfileWithMetadata
}

export async function updateChannelConfig(
  channelName: string,
  config: Record<string, unknown>
): Promise<void> {
  const { error } = await supabase.rpc('update_channel_config', {
    p_channel_name: channelName,
    p_config: config,
  })

  if (error) handleError(error)
}

export async function updateChannelSecret(
  channelName: string,
  secretFieldName: string,
  secretValue: string
): Promise<void> {
  const { error } = await supabase.rpc('update_channel_secret', {
    p_channel_name: channelName,
    p_secret_field_name: secretFieldName,
    p_secret_value: secretValue,
  })

  if (error) handleError(error)
}

// =============================================================================
// NOTIFICATION RULES
// =============================================================================

export async function getNotificationRules(): Promise<NotificationRuleWithMetadata[]> {
  const { data, error } = await supabase
    .from('notification_rules')
    .select('*')
    .order('created_at', { ascending: false })

  if (error) handleError(error)
  return data as NotificationRuleWithMetadata[]
}

export async function getNotificationRule(
  id: string
): Promise<NotificationRuleWithMetadata> {
  const { data, error } = await supabase
    .from('notification_rules')
    .select('*')
    .eq('id', id)
    .single()

  if (error) handleError(error)
  return data as NotificationRuleWithMetadata
}

export async function createNotificationRule(
  rule: CreateNotificationRuleRequest
): Promise<NotificationRule> {
  console.log('[API] createNotificationRule called with:', rule)
  try {
    // Use RPC function which handles tier limits and validation
    const { data, error } = await supabase.rpc('create_notification_rule', {
      p_name: rule.name,
      p_rule_type: rule.rule_type,
      p_rule_config: rule.rule_config,
      p_description: rule.description ?? null,
      p_channels: rule.channels ?? ['in_app'],
      p_text_filter: rule.text_filter ?? null,
    })

    console.log('[API] createNotificationRule response:', { data, error })
    if (error) handleError(error)
    return data as NotificationRule
  } catch (e) {
    console.error('[API] createNotificationRule exception:', e)
    throw e
  }
}

export async function updateNotificationRule(
  id: string,
  updates: UpdateNotificationRuleRequest
): Promise<NotificationRule> {
  // Use RPC function which handles tier limits and validation
  const { data, error } = await supabase.rpc('update_notification_rule', {
    p_rule_id: id,
    p_name: updates.name ?? null,
    p_description: updates.description ?? null,
    p_is_active: updates.is_active ?? null,
    p_rule_config: updates.rule_config ?? null,
    p_channels: updates.channels ?? null,
    // undefined leaves the filter unchanged; null removes it, sent as {} (a JS null arrives as SQL NULL)
    p_text_filter: updates.text_filter === undefined ? null : updates.text_filter ?? {},
  })

  if (error) handleError(error)
  return data as NotificationRule
}

export async function deleteNotificationRule(id: string): Promise<void> {
  // Use RPC function for deletion
  const { error } = await supabase.rpc('delete_notification_rule', {
    p_rule_id: id,
  })

  if (error) handleError(error)
}

export async function toggleNotificationRule(
  id: string,
  isActive: boolean
): Promise<NotificationRule> {
  return updateNotificationRule(id, { is_active: isActive })
}

/**
 * Parameters for paginated notification rules
 */
export interface NotificationRulesParams extends PaginationParams {
  status?: 'active' | 'inactive' | null
  ruleTypes?: string[]
}

/**
 * Get paginated notification rules with filters
 * Server-side pagination with status and rule type filtering
 */
export async function getPaginatedNotificationRules(
  params?: NotificationRulesParams
): Promise<PaginatedResponse<NotificationRuleWithMetadata>> {
  const { data, error } = await supabase.rpc('get_paginated_notification_rules', {
    p_page: params?.page ?? 1,
    p_limit: params?.limit ?? 25,
    p_status: params?.status ?? null,
    p_rule_types: params?.ruleTypes && params.ruleTypes.length > 0 ? params.ruleTypes : null,
  })

  if (error) handleError(error)

  // RPC returns { data: [], pagination: { page, limit, total } }
  const result = data as { data: NotificationRuleWithMetadata[]; pagination: { page: number; limit: number; total: number } }

  return {
    data: result.data ?? [],
    pagination: result.pagination ?? { page: params?.page ?? 1, limit: params?.limit ?? 25, total: 0 },
  }
}

// =============================================================================
// NOTIFICATIONS
// =============================================================================

export async function getNotifications(
  params?: PaginationParams & { unreadOnly?: boolean }
): Promise<PaginatedResponse<Notification>> {
  const page = params?.page ?? 1
  const limit = params?.limit ?? 20
  const from = (page - 1) * limit
  const to = from + limit - 1

  let query = supabase
    .from('notifications')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(from, to)

  if (params?.unreadOnly) {
    query = query.eq('is_read', false)
  }

  const { data, error, count } = await query

  if (error) handleError(error)

  return {
    data: data as Notification[],
    pagination: {
      page,
      limit,
      total: count ?? 0,
    },
  }
}

export async function getNotification(id: string): Promise<Notification> {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('id', id)
    .single()

  if (error) handleError(error)
  return data as Notification
}

export async function markNotificationRead(id: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('mark_notification_read', {
    p_notification_id: id,
  })

  if (error) handleError(error)
  return data as boolean
}

export async function markAllNotificationsRead(): Promise<number> {
  const { data, error } = await supabase.rpc('mark_all_notifications_read')

  if (error) handleError(error)
  return data as number
}

export async function getUnreadNotificationCount(): Promise<number> {
  const { data, error } = await supabase.rpc('get_unread_count')

  if (error) handleError(error)
  return data ?? 0
}

// =============================================================================
// INBOX
// =============================================================================

export async function markInboxRead(inboxId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('mark_inbox_read', {
    p_inbox_id: inboxId,
  })

  if (error) handleError(error)
  return data as boolean
}

export async function markAllInboxRead(): Promise<number> {
  const { data, error } = await supabase.rpc('mark_all_inbox_read')

  if (error) handleError(error)
  return data as number
}

export async function getUnreadInboxCount(): Promise<number> {
  const { data, error } = await supabase.rpc('get_unread_inbox_count')

  if (error) handleError(error)
  return data ?? 0
}

export interface InboxParams extends PaginationParams {
  messageType?: 'alert' | 'system' | null
  searchQuery?: string
  readStatus?: 'all' | 'unread' | 'read'
}

export async function getInbox(
  params?: InboxParams
): Promise<PaginatedResponse<InboxMessage>> {
  const page = params?.page ?? 1
  const limit = params?.limit ?? 20
  const from = (page - 1) * limit
  const to = from + limit - 1

  let query = supabase
    .from('inbox')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .range(from, to)

  if (params?.messageType) {
    query = query.eq('message_type', params.messageType)
  }

  if (params?.searchQuery) {
    query = query.ilike('title', `%${params.searchQuery}%`)
  }

  if (params?.readStatus === 'unread') {
    query = query.eq('is_read', false)
  } else if (params?.readStatus === 'read') {
    query = query.eq('is_read', true)
  }

  const { data, error, count } = await query

  if (error) handleError(error)

  return {
    data: data as InboxMessage[],
    pagination: {
      page,
      limit,
      total: count ?? 0,
    },
  }
}

export async function getInboxMessage(
  id: string
): Promise<InboxMessage | null> {
  const { data, error } = await supabase
    .from('inbox')
    .select('*')
    .eq('id', id)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null // Not found
    handleError(error)
  }
  return data as InboxMessage
}

export interface InboxStats {
  '24h': number
  '7d': number
  '30d': number
}

export async function getInboxStats(): Promise<InboxStats> {
  const { data, error } = await supabase.rpc('get_my_inbox_stats')

  if (error) handleError(error)
  return (data as InboxStats) ?? { '24h': 0, '7d': 0, '30d': 0 }
}

export async function getRecentInbox(
  limit: number = 5
): Promise<InboxMessage[]> {
  const { data, error } = await supabase
    .from('inbox')
    .select('*')
    .order('is_read', { ascending: true })
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) handleError(error)
  return data as InboxMessage[]
}

// =============================================================================
// ALERT TIMELINE (Dashboard Chart)
// =============================================================================

export interface AlertTimelinePoint {
  day: string
  count: number
}

export async function getAlertTimeline(params?: {
  ruleIds?: string[]
  days?: number
}): Promise<AlertTimelinePoint[]> {
  const { data, error } = await supabase.rpc('get_my_alert_timeline', {
    p_rule_ids: params?.ruleIds && params.ruleIds.length > 0 ? params.ruleIds : null,
    p_days: params?.days ?? 30,
  })

  if (error) handleError(error)
  return (data as AlertTimelinePoint[]) ?? []
}

// =============================================================================
// GROUPED NOTIFICATIONS (Dashboard)
// =============================================================================

export interface GroupedNotificationsParams extends PaginationParams {
  ruleIds?: string[]
  unreadOnly?: boolean
  readOnly?: boolean
  dateFrom?: string  // ISO date string YYYY-MM-DD
  dateTo?: string    // ISO date string YYYY-MM-DD
}

// Rule with notification stats for filter dropdown
export interface RuleWithNotificationStats {
  rule_id: string
  name: string
  rule_type: string
  is_active: boolean
  alert_count: number
  last_alert_at: string | null
}

/**
 * Get notifications grouped by (publication_id, notification_rule_id).
 * Grouping and deduplication happens in the database, not client-side.
 *
 * @param params - Filter and pagination parameters
 * @returns Paginated array of TimelineNotification with channels array
 */
export async function getGroupedNotifications(
  params?: GroupedNotificationsParams
): Promise<PaginatedResponse<TimelineNotification>> {
  const { data, error } = await supabase.rpc('get_grouped_notifications', {
    p_rule_ids: params?.ruleIds ?? null,
    p_unread_only: params?.unreadOnly ?? false,
    p_read_only: params?.readOnly ?? false,
    p_page: params?.page ?? 1,
    p_limit: params?.limit ?? 20,
    p_date_from: params?.dateFrom ?? null,
    p_date_to: params?.dateTo ?? null,
  })

  if (error) handleError(error)

  // RPC returns { data: [], pagination: { page, limit, total } }
  const result = data as { data: TimelineNotification[]; pagination: { page: number; limit: number; total: number } }

  return {
    data: result.data ?? [],
    pagination: result.pagination ?? { page: params?.page ?? 1, limit: params?.limit ?? 20, total: 0 },
  }
}

/**
 * Get notification rules with alert statistics for filter dropdown.
 * Returns rules that have at least 1 alert, sorted by most recent alert.
 *
 * @returns Array of rules with alert counts and last alert timestamps
 */
export async function getRulesWithNotificationStats(): Promise<RuleWithNotificationStats[]> {
  const { data, error } = await supabase.rpc('get_rules_with_notification_stats')

  if (error) handleError(error)

  return (data as RuleWithNotificationStats[]) ?? []
}

/**
 * Mark an alert as read by alert_id.
 *
 * @param alertId - The alert UUID
 * @returns true if marked, false if already read or not found
 */
export async function markAlertRead(alertId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('mark_alert_read', {
    p_alert_id: alertId,
  })

  if (error) handleError(error)
  return data as boolean
}

/**
 * Mark an alert as read (accepts alert_id directly).
 * This is the primary method - use this for marking grouped notifications as read.
 *
 * @param alertId - The alert UUID
 * @returns Number of alerts marked as read (0 or 1)
 */
export async function markGroupedNotificationRead(alertId: string): Promise<number> {
  const { data, error } = await supabase.rpc('mark_grouped_notification_read', {
    p_alert_id: alertId,
  })

  if (error) handleError(error)
  return data as number
}

/**
 * Get detailed information for an alert by alert_id.
 * Returns all channel notifications, delivery attempts, and metadata.
 *
 * @param alertId - The alert UUID
 * @returns Alert detail with all channels and delivery attempts
 */
export async function getAlertDetails(alertId: string): Promise<AlertDetail | null> {
  const { data, error } = await supabase.rpc('get_alert_details', {
    p_alert_id: alertId,
  })

  if (error) handleError(error)
  return data as AlertDetail | null
}

// =============================================================================
// PUBLICATIONS (ARCHIVE)
// =============================================================================

export interface PublicationSearchParams extends PaginationParams {
  query?: string
  categoryId?: string
  publicationTypeId?: string
  institutionName?: string
  dateFrom?: string
  dateTo?: string
  sortBy?: 'issue_date' | 'created_at'
  sortOrder?: 'asc' | 'desc'
}

// =============================================================================
// ARCHIVE FILTERS (v2)
// =============================================================================

export interface PublicationYear {
  year: number
  count: number
}

export interface PublicationIssue {
  issue_number: string
  issue_date: string
  count: number
}

export interface PublicationSearchParamsV2 extends PaginationParams {
  query?: string
  categoryIds?: string[]           // Category IDs (UUIDs)
  publicationTypeIds?: string[]    // Type IDs (UUIDs, includes children)
  issueNumbers?: string[]          // Multi-select issues
  years?: number[]                 // Multi-select years
  dateFrom?: string
  dateTo?: string
  institutionIds?: string[]        // Discovered institution IDs (UUIDs)
  bookmarksOnly?: boolean
}

export async function searchPublications(
  params?: PublicationSearchParams
): Promise<PaginatedResponse<Publication>> {
  const page = params?.page ?? 1
  const limit = params?.limit ?? 20

  // Use RPC function which handles full-text search properly
  const { data, error } = await supabase.rpc('search_publications', {
    p_query: params?.query ?? null,
    p_category_id: params?.categoryId ?? null,
    p_publication_type_id: params?.publicationTypeId ?? null,
    p_institution_name: params?.institutionName ?? null,
    p_date_from: params?.dateFrom ?? null,
    p_date_to: params?.dateTo ?? null,
    p_page: page,
    p_limit: limit,
  })

  if (error) handleError(error)

  // RPC returns { data: [], pagination: { page, limit, total } }
  const result = data as { data: Publication[]; pagination: { page: number; limit: number; total: number } }

  return {
    data: result.data ?? [],
    pagination: result.pagination ?? { page, limit, total: 0 },
  }
}

export async function searchPublicationsBySemantic(
  params: { recipeName: string; matchCriteria: Record<string, unknown>; page?: number; limit?: number }
): Promise<PaginatedResponse<Publication>> {
  const page = params.page ?? 1
  const limit = params.limit ?? 5

  const { data, error } = await supabase.rpc('search_publications_by_semantic', {
    p_recipe_name: params.recipeName,
    p_match_criteria: params.matchCriteria,
    p_page: page,
    p_limit: limit,
  })

  if (error) handleError(error)

  const result = data as { data: Publication[]; pagination: { page: number; limit: number; total: number } }

  return {
    data: result.data ?? [],
    pagination: result.pagination ?? { page, limit, total: 0 },
  }
}

/**
 * What a draft rule would have caught over the last 30 days: the Source's count,
 * the filter's, sample matches with their Matched Terms and the rows the filter
 * drops. The server matches as the alerts do.
 */
export async function previewNotificationRule(
  ruleType: string,
  ruleConfig: Record<string, unknown>,
  textFilter: TextFilter | null = null,
  limit = 5
): Promise<RulePreview> {
  const { data, error } = await supabase.rpc('preview_notification_rule', {
    p_rule_type: ruleType,
    p_rule_config: ruleConfig,
    p_text_filter: textFilter,
    p_limit: limit,
  })

  if (error) handleError(error)

  return data as unknown as RulePreview
}

/**
 * A Text filter's verdict (faulty with its reason, rare or sound) and its
 * breakdown over the past year: each Term's hits in the Source, up to 3
 * narrower Sources where the Terms hit, and for a narrow all-of rule what Поне
 * една would catch. Takes a draft or saved rule.
 */
export async function getRuleFilterBreakdown(
  ruleType: string,
  ruleConfig: Record<string, unknown>,
  textFilter: TextFilter | null
): Promise<RuleFilterBreakdown> {
  const { data, error } = await supabase.rpc('rule_filter_breakdown', {
    p_rule_type: ruleType,
    p_rule_config: ruleConfig,
    p_text_filter: textFilter,
  })

  if (error) handleError(error)

  return data as unknown as RuleFilterBreakdown
}

/** Dismiss a Faulty filter's badge until the rule's Source or Text filter next changes */
export async function dismissFaultyFilter(id: string): Promise<NotificationRuleWithMetadata> {
  const { data, error } = await supabase.rpc('dismiss_faulty_filter', { p_rule_id: id })

  if (error) handleError(error)
  return data as unknown as NotificationRuleWithMetadata
}

export async function getPublication(id: string): Promise<Publication> {
  console.log('[API] getPublication called with id:', id)
  try {
    const { data, error } = await supabase
      .from('publications')
      .select('*')
      .eq('id', id)
      .single()

    console.log('[API] getPublication response:', { data, error })
    if (error) handleError(error)
    return data as Publication
  } catch (e) {
    console.error('[API] getPublication exception:', e)
    throw e
  }
}

/**
 * Get available publication years with counts
 * Used by archive page issue filter hierarchy
 */
export async function getPublicationYears(): Promise<PublicationYear[]> {
  const { data, error } = await supabase.rpc('get_publication_years')

  if (error) handleError(error)
  return (data as PublicationYear[]) ?? []
}

/**
 * Get issues for a specific year with counts
 * Used by archive page issue filter (3-level hierarchy)
 */
export async function getIssuesByYear(year: number): Promise<PublicationIssue[]> {
  const { data, error } = await supabase.rpc('get_issues_by_year', {
    p_year: year,
  })

  if (error) handleError(error)
  return (data as PublicationIssue[]) ?? []
}

/**
 * Enhanced publication search with multi-select support
 * Used by archive page with hierarchical filters
 */
export async function searchPublicationsV2(
  params?: PublicationSearchParamsV2
): Promise<PaginatedResponse<Publication>> {
  const page = params?.page ?? 1
  const limit = params?.limit ?? 20

  const { data, error } = await supabase.rpc('search_publications_v2', {
    p_query: params?.query ?? null,
    p_category_ids: params?.categoryIds && params.categoryIds.length > 0 ? params.categoryIds : null,
    p_publication_type_ids: params?.publicationTypeIds && params.publicationTypeIds.length > 0 ? params.publicationTypeIds : null,
    p_issue_numbers: params?.issueNumbers && params.issueNumbers.length > 0 ? params.issueNumbers : null,
    p_years: params?.years && params.years.length > 0 ? params.years : null,
    p_date_from: params?.dateFrom ?? null,
    p_date_to: params?.dateTo ?? null,
    p_institution_ids: params?.institutionIds && params.institutionIds.length > 0 ? params.institutionIds : null,
    p_page: page,
    p_limit: limit,
    p_bookmarks_only: params?.bookmarksOnly ?? false,
  })

  if (error) handleError(error)

  // RPC returns { data: [], pagination: { page, limit, total } }
  const result = data as { data: Publication[]; pagination: { page: number; limit: number; total: number } }

  return {
    data: result.data ?? [],
    pagination: result.pagination ?? { page, limit, total: 0 },
  }
}

// =============================================================================
// BOOKMARKS
// =============================================================================

export async function addBookmark(publicationId: string): Promise<void> {
  const { error } = await supabase.rpc('add_bookmark', { p_publication_id: publicationId })
  if (error) handleError(error)
}

export async function removeBookmark(publicationId: string): Promise<void> {
  const { error } = await supabase.rpc('remove_bookmark', { p_publication_id: publicationId })
  if (error) handleError(error)
}

export async function getMyBookmarks(): Promise<string[]> {
  const { data, error } = await supabase.rpc('get_my_bookmarks')
  if (error) handleError(error)
  return (data ?? []).map((row: { publication_id: string }) => row.publication_id)
}

// =============================================================================
// REFERENCE DATA
// =============================================================================

export async function getInstitutionCategories(): Promise<InstitutionCategory[]> {
  const { data, error } = await supabase
    .from('institution_categories')
    .select('*')
    .order('display_order')

  if (error) handleError(error)
  return data as InstitutionCategory[]
}

export async function getPublicationTypes(): Promise<PublicationType[]> {
  const { data, error } = await supabase
    .from('publication_types')
    .select('*')
    .order('display_order')

  if (error) handleError(error)
  return data as PublicationType[]
}

export async function getCategoryPublicationTypes(): Promise<CategoryPublicationType[]> {
  const { data, error } = await supabase
    .from('category_publication_types')
    .select('*')

  if (error) handleError(error)
  return data as CategoryPublicationType[]
}

export async function getExtractionRecipes(): Promise<ExtractionRecipe[]> {
  const { data, error } = await supabase.rpc('get_extraction_recipes')
  if (error) handleError(error)
  return (data ?? []) as ExtractionRecipe[]
}

export async function getPublicationExtractions(publicationId: string): Promise<PublicationExtraction[]> {
  const { data, error } = await supabase.rpc('get_publication_extractions', {
    p_publication_id: publicationId,
  })
  if (error) handleError(error)
  return (data ?? []) as PublicationExtraction[]
}

export interface DiscoveredInstitution {
  id: string
  name: string
  category_id: string | null
  mention_count: number
}

export async function searchInstitutions(query: string, limit = 20): Promise<DiscoveredInstitution[]> {
  if (!query || query.length < 2) return []
  const { data, error } = await supabase.rpc('search_institutions', {
    p_query: query,
    p_limit: limit,
  })
  if (error) handleError(error)
  return (data ?? []) as DiscoveredInstitution[]
}

export async function listInstitutions(limit = 500): Promise<DiscoveredInstitution[]> {
  const { data, error } = await supabase.rpc('list_institutions', {
    p_limit: limit,
  })
  if (error) handleError(error)
  return (data ?? []) as DiscoveredInstitution[]
}

/**
 * The Institution list for an institution Source (#138): every Institution, or
 * with a query those whose name holds it or one of whose aliases starts with it
 * (alias hits first), most published first.
 */
export async function listRuleInstitutions(query?: string): Promise<RuleInstitution[]> {
  const { data, error } = await supabase.rpc('list_rule_institutions', {
    p_query: query?.trim() || null,
  })
  if (error) handleError(error)
  return (data ?? []) as RuleInstitution[]
}

/** The Institutions of a publisher name, in order (a Sub-unit gives its Institution) */
export async function getPublisherInstitutions(publisherName: string): Promise<string[]> {
  const { data, error } = await supabase.rpc('publisher_institutions', {
    p_publisher_name: publisherName,
  })
  if (error) handleError(error)
  return (data ?? []) as string[]
}

/** A law by its identity: name + promulgation (amending acts of one law share a name). */
export interface DiscoveredLaw {
  id: string
  name: string
  /** ДВ issue and year the law was promulgated in; null until an extraction cites them. */
  dv_issue: number | null
  dv_year: number | null
  /** An amending act (ЗИД, ЗИ, ЗД). */
  is_amending_act: boolean
  mention_count: number
}

export async function searchLaws(query: string, limit = 20, includeAmendingActs = true): Promise<DiscoveredLaw[]> {
  if (!query || query.length < 2) return []
  const { data, error } = await supabase.rpc('search_laws', {
    p_query: query,
    p_limit: limit,
    p_include_amending_acts: includeAmendingActs,
  })
  if (error) handleError(error)
  return (data ?? []) as DiscoveredLaw[]
}

export async function listLaws(limit = 500, includeAmendingActs = true): Promise<DiscoveredLaw[]> {
  const { data, error } = await supabase.rpc('list_laws', {
    p_limit: limit,
    p_include_amending_acts: includeAmendingActs,
  })
  if (error) handleError(error)
  return (data ?? []) as DiscoveredLaw[]
}

// =============================================================================
// BILLING
// =============================================================================

export async function createCheckoutSession(priceId: string): Promise<string> {
  const { data, error } = await supabase.functions.invoke('billing', {
    body: { action: 'checkout', priceId },
  })

  if (error) handleError(error)
  return data.url
}

export async function getBillingPortalUrl(): Promise<string> {
  const { data, error } = await supabase.functions.invoke('billing', {
    body: { action: 'portal' },
  })

  if (error) handleError(error)
  return data.url
}

// =============================================================================
// WEBHOOK
// =============================================================================

export interface DeliveryAttempt {
  id: string
  created_at: string
  event_type: string
  status_code: number | null
  response_time_ms: number | null
  error_message: string | null
  request_payload: Record<string, unknown> | null
  response_body: string | null
}

export interface WebhookSecretResponse {
  secret: string
  message?: string
}

export interface WebhookTestResult {
  success: boolean
  error?: string
}

export async function getWebhookHistory(limit: number = 20): Promise<DeliveryAttempt[]> {
  const { data, error } = await supabase
    .from('delivery_attempts')
    .select('*')
    .eq('channel', 'webhook')
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) handleError(error)
  return data as DeliveryAttempt[]
}

export async function regenerateWebhookSecret(): Promise<WebhookSecretResponse> {
  const { data, error } = await supabase.rpc('regenerate_webhook_secret')

  if (error) handleError(error)
  return data as WebhookSecretResponse
}

export async function getWebhookSecret(): Promise<WebhookSecretResponse> {
  const { data, error } = await supabase.rpc('get_webhook_secret')

  if (error) handleError(error)
  return data as WebhookSecretResponse
}

export async function sendWebhookTest(webhookUrl: string): Promise<WebhookTestResult> {
  const { data, error, response } = await supabase.functions.invoke('app-webhook-test', {
    body: { webhook_url: webhookUrl },
  })

  if (error) {
    if (response?.status === 429) {
      throw new ApiError('Rate limit exceeded. Please wait before trying again.', 'RATE_LIMITED')
    }
    handleError(error)
  }
  return data as WebhookTestResult
}

// =============================================================================
// ACCOUNT
// =============================================================================

export interface DataExportResult {
  status: 'pending' | 'processing' | 'ready' | 'error' | 'expired' | 'none'
  export_id?: string
  download_url?: string | null
  expires_at?: string | null
  requested_at?: string
  file_size_bytes?: number | null
  stages_completed?: number
  stages_failed?: number
  stages_total?: number
  error?: string | null
}

/**
 * Request a new data export. Creates a pending record via RPC.
 * Returns existing export if one is already in progress.
 * Rate-limited to 1 per hour (enforced server-side).
 */
export async function requestDataExport(): Promise<DataExportResult> {
  const { data, error } = await supabase.rpc('create_data_export')

  if (error) {
    if (error.message?.includes('rate_limit_exceeded')) {
      const match = error.message.match(/rate_limit_exceeded:(.+)/)
      const retryAfter = match?.[1] ? new Date(match[1]).toISOString() : undefined
      throw new ApiError('RATE_LIMITED', 'RATE_LIMITED', retryAfter)
    }
    handleError(error)
  }

  return {
    status: 'pending',
    export_id: data as string,
    stages_completed: 0,
    stages_failed: 0,
    stages_total: 7,
  }
}

/**
 * Get the latest data export status via RPC.
 * Returns download_url directly from DB (pre-signed at assembly time).
 */
export async function getDataExportStatus(): Promise<DataExportResult> {
  const { data, error } = await supabase.rpc('get_latest_data_export')

  if (error) handleError(error)

  const rows = data as Array<{
    id: string
    status: string
    stages_completed: number
    stages_failed: number
    file_path: string | null
    download_url: string | null
    file_size_bytes: number | null
    expires_at: string | null
    requested_at: string
    completed_at: string | null
    error_message: string | null
  }> | null

  if (!rows || rows.length === 0) {
    return { status: 'none' }
  }

  const row = rows[0]

  // Check for expired ready exports
  const isExpired = row.status === 'ready' && row.expires_at && new Date(row.expires_at) <= new Date()

  return {
    status: (isExpired ? 'expired' : row.status) as DataExportResult['status'],
    export_id: row.id,
    download_url: row.download_url,
    expires_at: row.expires_at,
    requested_at: row.requested_at,
    file_size_bytes: row.file_size_bytes,
    stages_completed: row.stages_completed,
    stages_failed: row.stages_failed,
    stages_total: 7,
    error: row.error_message,
  }
}

export async function deleteAccount(): Promise<void> {
  const { error } = await supabase.functions.invoke('app-delete-account', {
    body: {},
  })

  if (error) handleError(error)
}
